import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import { posApi, operationApi } from '../../services/api';
import { Bar } from 'react-chartjs-2';
import InvoiceModal from '../../components/admin/InvoiceModal';
import { QrCode, UserCheck, Tag, Eye, X, Printer, MessageCircle, Timer, Clock, ListChecks, TrendingUp, Users, BarChart3, ShoppingCart, ClipboardList, FolderOpen, Boxes, User } from 'lucide-react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const STATUSES = ['pending', 'assigned', 'waiting', 'confirmed', 'on_progress', 'done', 'invoice_release', 'paid', 'invoice_outstanding', 'cancelled'];
const PAYMENT_STATUSES = ['unpaid', 'outstanding', 'paid', 'refunded'];

const statusLabels = {
  pending: 'Pending', assigned: 'Diassign', waiting: 'Waiting', confirmed: 'Confirmed',
  on_progress: 'On Progress', done: 'Done', invoice_release: 'Invoice Release',
  paid: 'Paid', invoice_outstanding: 'Invoice Outstanding', cancelled: 'Cancelled',
};
const paymentLabels = { unpaid: 'Belum Bayar', outstanding: 'Outstanding', paid: 'Lunas', refunded: 'Refund' };

const statusColors = {
  pending: 'bg-yellow-100 text-yellow-700', assigned: 'bg-indigo-100 text-indigo-700',
  waiting: 'bg-orange-100 text-orange-700', confirmed: 'bg-blue-100 text-blue-700',
  on_progress: 'bg-purple-100 text-purple-700', done: 'bg-green-100 text-green-700',
  invoice_release: 'bg-teal-100 text-teal-700', paid: 'bg-emerald-100 text-emerald-700',
  invoice_outstanding: 'bg-red-100 text-red-700', cancelled: 'bg-gray-100 text-gray-700',
};
const paymentColors = {
  unpaid: 'bg-red-100 text-red-700', outstanding: 'bg-orange-100 text-orange-700',
  paid: 'bg-green-100 text-green-700', refunded: 'bg-gray-100 text-gray-700',
};

export default function DashboardPage() {
  const [stats, setStats] = useState({ orders: 0, revenue: 0, customers: 0, avgOrder: 0 });
  const [monthlySales, setMonthlySales] = useState({ labels: [], data: [] });
  const [recentOrders, setRecentOrders] = useState([]);
  const [allOrders, setAllOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detailOrder, setDetailOrder] = useState(null);
  const [workers, setWorkers] = useState([]);
  const [invoiceOrder, setInvoiceOrder] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        const now = new Date();
        const firstDay = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
        const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];

        const [ordersRes] = await Promise.all([
          posApi.orders.list({ per_page: 100, date_from: firstDay, date_to: lastDay }),
        ]);

        const orders = ordersRes.data.data?.data || ordersRes.data.data || [];
        const totalRevenue = orders.reduce((sum, o) => sum + (parseFloat(o.total) || 0), 0);
        const uniquePhones = new Set(orders.map(o => o.customer_phone).filter(Boolean));

        setStats({ orders: orders.length, revenue: totalRevenue, customers: uniquePhones.size, avgOrder: orders.length > 0 ? Math.round(totalRevenue / orders.length) : 0 });
        setAllOrders(orders);
        setRecentOrders(orders.slice(0, 20));

        const months = [];
        const salesData = [];
        for (let i = 5; i >= 0; i--) {
          const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
          months.push(d.toLocaleDateString('id-ID', { month: 'short', year: 'numeric' }));
          salesData.push(0);
        }
        try {
          const sixMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 5, 1).toISOString().split('T')[0];
          const allRes = await posApi.orders.list({ per_page: 500, date_from: sixMonthsAgo, date_to: lastDay });
          const allOrdersData = allRes.data.data?.data || allRes.data.data || [];
          allOrdersData.forEach(o => {
            const oDate = new Date(o.created_at);
            const monthIdx = (oDate.getFullYear() - now.getFullYear()) * 12 + (oDate.getMonth() - now.getMonth()) + 5;
            if (monthIdx >= 0 && monthIdx < 6) salesData[monthIdx] += parseFloat(o.total) || 0;
          });
        } catch {}
        setMonthlySales({ labels: months, data: salesData });
      } catch (err) { console.error('Dashboard fetch error:', err); } finally { setLoading(false); }
    };
    fetchDashboard();
  }, []);

  useEffect(() => {
    const loadWorkers = async () => {
      try { const res = await operationApi.profiles.list({ per_page: 200 }); setWorkers(res.data.data?.data || res.data.data || []); } catch {}
    };
    loadWorkers();
  }, []);

  const formatPrice = (p) => new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(p);
  const fmtDate = (d) => new Date(d).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

  const handleStatusUpdate = async (orderId, field, value) => {
    setSaving(true);
    try {
      await posApi.orders.update(orderId, { [field]: value });
      const updated = { ...detailOrder, [field]: value };
      setDetailOrder(updated);
      setRecentOrders(prev => prev.map(o => o.id === orderId ? { ...o, [field]: value } : o));
    } catch (err) { alert('Gagal update status'); }
    finally { setSaving(false); }
  };

  const handleAssignWorker = async (orderId, workerId) => {
    setSaving(true);
    try {
      await posApi.orders.update(orderId, { assigned_worker_id: workerId, status: 'assigned' });
      const worker = workers.find(w => w.id === workerId);
      setDetailOrder(prev => ({ ...prev, assigned_worker_id: workerId, status: 'assigned', assigned_worker: worker }));
      setRecentOrders(prev => prev.map(o => o.id === orderId ? { ...o, assigned_worker_id: workerId, status: 'assigned', assigned_worker: worker } : o));
    } catch (err) { alert('Gagal assign worker'); }
    finally { setSaving(false); }
  };

  const chartData = { labels: monthlySales.labels, datasets: [{ label: 'Penjualan', data: monthlySales.data, backgroundColor: 'rgba(0, 179, 166, 0.5)', borderColor: '#00B3A6', borderWidth: 1, borderRadius: 8 }] };
  const chartOptions = { responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false }, tooltip: { callbacks: { label: (ctx) => formatPrice(ctx.raw) } } }, scales: { y: { beginAtZero: true, ticks: { callback: (v) => v >= 1000000 ? (v / 1000000).toFixed(1) + 'M' : v >= 1000 ? (v / 1000).toFixed(0) + 'K' : v } } } };

  // Live timer for on_progress orders
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, []);

  function formatElapsed(startedAt) {
    if (!startedAt) return '-';
    const start = new Date(startedAt).getTime();
    const elapsed = Math.floor((now - start) / 1000);
    if (elapsed < 0) return '0:00';
    const h = Math.floor(elapsed / 3600);
    const m = Math.floor((elapsed % 3600) / 60);
    const s = elapsed % 60;
    if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    return `${m}:${String(s).padStart(2, '0')}`;
  }

  if (loading) {
    return <AdminLayout active="Dashboard"><div className="flex justify-center py-20"><div className="w-10 h-10 border-4 border-t-transparent rounded-full animate-spin" style={{ borderColor: '#00B3A6', borderTopColor: 'transparent' }} /></div></AdminLayout>;
  }

  return (
    <AdminLayout active="Dashboard">
      <h1 className="text-lg font-semibold text-gray-800 mb-1">Dashboard</h1>
      <p className="text-sm text-gray-500 mb-6">Ringkasan penjualan bulan {new Date().toLocaleDateString('id-ID', { month: 'long', year: 'numeric' })}</p>

      {/* Stat Cards - 1 Row (4 columns) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Total Pesanan', value: stats.orders, Icon: ListChecks, color: 'bg-blue-50 text-blue-600' },
          { label: 'Pendapatan', value: formatPrice(stats.revenue), Icon: TrendingUp, color: 'bg-green-50 text-green-600' },
          { label: 'Pelanggan', value: stats.customers, Icon: Users, color: 'bg-purple-50 text-purple-600' },
          { label: 'Rata-rata Pesanan', value: formatPrice(stats.avgOrder), Icon: BarChart3, color: 'bg-orange-50 text-orange-600' },
        ].map(card => (
          <div key={card.label} className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className={`w-10 h-10 rounded-xl flex items-center justify-center ${card.color}`}><card.Icon size={20} /></span>
                <p className="text-sm text-gray-500">{card.label}</p>
              </div>
              <p className="text-lg font-bold text-gray-900">{card.value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Shortcut Menu Cards - Above Pesanan Terbaru */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {[
          { to: '/admin/pos', label: 'POS', Icon: ShoppingCart, color: 'bg-blue-50 text-blue-600' },
          { to: '/admin/orders', label: 'PESANAN', Icon: ClipboardList, color: 'bg-green-50 text-green-600' },
          { to: '/admin/layanan', label: 'LAYANAN', Icon: FolderOpen, color: 'bg-purple-50 text-purple-600' },
          { to: '/admin/packages', label: 'PAKET', Icon: Boxes, color: 'bg-orange-50 text-orange-600' },
        ].map(item => (
          <Link key={item.label} to={item.to} className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <span className={`w-10 h-10 rounded-xl flex items-center justify-center ${item.color}`}><item.Icon size={20} /></span>
              <p className="font-semibold text-gray-900 text-sm">{item.label}</p>
            </div>
          </Link>
        ))}
      </div>

      {/* Pesanan Terbaru - Full Width */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Clock size={18} className="text-gray-600" />
            <h2 className="font-bold text-gray-800">Pesanan Terbaru</h2>
          </div>
          <p className="text-xs text-gray-500 font-medium">Max 20</p>
        </div>
        {recentOrders.length === 0 ? <p className="text-sm text-gray-400 text-center py-8">Belum ada pesanan bulan ini</p> : (
          <div className="space-y-2">
            {recentOrders.map(o => (
              <div key={o.id} className="flex items-center justify-between p-3 bg-gray-50 hover:bg-brand/5 rounded-lg transition-colors border border-transparent hover:border-brand/20 gap-2">
                {/* Left: Customer Info */}
                <button onClick={() => setDetailOrder(o)} className="flex-1 min-w-0 text-left flex items-center gap-2">
                  <User size={16} className="text-gray-400 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-800 truncate">{o.customer_name || 'Tanpa Nama'} <span className="text-[10px] text-gray-400 font-mono">{o.order_number}</span></p>
                    <p className="text-xs text-gray-400">{new Date(o.created_at).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</p>
                  </div>
                </button>
                {/* Right: Price & Status */}
                <div className="text-right flex-shrink-0">
                  <span className="text-sm font-bold text-navy block">{formatPrice(o.total)}</span>
                  <span className={`block text-[10px] rounded-full px-1.5 py-0.5 mt-0.5 font-medium ${statusColors[o.status] || 'bg-gray-100 text-gray-700'}`}>{statusLabels[o.status] || o.status}</span>
                </div>
                {/* Action Buttons */}
                <div className="flex items-center gap-1 flex-shrink-0">
                  {/* WhatsApp Button */}
                  <a
                    href={`https://wa.me/${(o.customer_phone || '').replace(/[^0-9]/g, '')}?text=${encodeURIComponent(`Selamat datang di Klinnovac! 🎉\n\nHalo ${o.customer_name},\n\nTerima kasih telah memesan layanan kami.\n\nNomor Pesanan: ${o.order_number}`)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-1.5 hover:bg-green-100 rounded-lg transition-colors text-green-600 hover:text-green-700"
                    title="Hubungi via WhatsApp"
                  >
                    <MessageCircle size={16} />
                  </a>
                  {/* View Button */}
                  <button
                    onClick={() => setDetailOrder(o)}
                    className="p-1.5 hover:bg-gray-200 rounded-lg transition-colors text-gray-400 hover:text-gray-600"
                    title="Lihat detail"
                  >
                    <Eye size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
        <Link to="/admin/orders" className="block mt-3 text-center text-sm text-brand hover:text-brand-dark font-medium">Lihat semua pesanan →</Link>
      </div>

      {/* Active On-Progress Orders - Live Timer Cards */}
      {(() => {
        const activeOrders = allOrders.filter(o => o.status === 'on_progress');
        if (activeOrders.length === 0) return null;
        return (
          <div className="mb-6">
            <h2 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
              <Timer size={18} className="text-purple-600 animate-pulse" /> Sedang Dikerjakan ({activeOrders.length})
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {activeOrders.map(o => {
                const elapsed = formatElapsed(o.started_at);
                const worker = o.assigned_worker || workers.find(w => w.id === o.assigned_worker_id);
                return (
                  <button
                    key={o.id}
                    onClick={() => setDetailOrder(o)}
                    className="bg-gradient-to-r from-purple-50 to-violet-50 rounded-xl p-4 border border-purple-200 shadow-sm hover:shadow-md transition-all text-left"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[10px] font-mono text-purple-600">{o.order_number}</span>
                      <span className="text-[9px] bg-purple-200 text-purple-700 rounded-full px-2 py-0.5 font-medium">ON PROGRESS</span>
                    </div>
                    <p className="text-sm font-semibold text-gray-800 truncate">{o.customer_name}</p>
                    <div className="mt-2 flex items-center justify-between">
                      <div>
                        <p className="text-[10px] text-purple-500 uppercase font-semibold">Timer</p>
                        <p className="text-lg font-bold text-purple-700 font-mono">{elapsed}</p>
                      </div>
                      {worker && (
                        <div className="text-right">
                          <p className="text-[10px] text-gray-400">Worker</p>
                          <p className="text-xs font-medium text-indigo-600">{worker.full_name}</p>
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* Chart - Penjualan 6 Bulan Terakhir */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 mb-6">
        <h2 className="font-bold text-gray-800 mb-4">📈 Penjualan 6 Bulan Terakhir</h2>
        <div className="h-64">{monthlySales.data.some(v => v > 0) ? <Bar data={chartData} options={chartOptions} /> : <div className="flex items-center justify-center h-full text-gray-400 text-sm">Belum ada data penjualan</div>}</div>
      </div>

      {/* Detail Popup with Status + Assign */}
      {detailOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-6xl max-h-[95vh] overflow-y-auto">
            <div className="flex justify-between items-center p-6 border-b sticky top-0 bg-white z-10">
              <div><h2 className="text-xl font-bold text-gray-800">{detailOrder.order_number}</h2><p className="text-sm text-gray-400">{fmtDate(detailOrder.created_at)}</p></div>
              <button onClick={() => setDetailOrder(null)} className="text-gray-400 hover:text-gray-600"><X size={24} /></button>
            </div>
            <div className="p-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* LEFT COLUMN: Customer Info & Controls */}
              <div className="space-y-4">
                {/* Customer Info */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-gray-600 mb-3">👤 Data Pelanggan</h3>
                  <div className="space-y-3 text-sm">
                    <div>
                      <p className="text-xs text-gray-400">Pelanggan</p>
                      <p className="font-semibold text-gray-800">{detailOrder.customer_name}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400">Telepon</p>
                      <a
                        href={(() => {
                          const phone = (detailOrder.customer_phone || '').replace(/[^0-9]/g, '');
                          const items = detailOrder.items || [];
                          const itemLines = items.map((item, idx) => `${idx + 1}. ${item.name} ×${item.quantity || 1} = ${formatPrice(item.unit_price * (item.quantity || 1))}${item.duration ? ' — ' + item.duration + ' menit' : ''}`).join('\n');
                          const totalDuration = items.reduce((sum, item) => sum + (parseInt(item.duration) || 0) * (item.quantity || 1), 0);
                          const text = encodeURIComponent(
                            `Halo ${detailOrder.customer_name},\n\n` +
                            `Terima kasih telah memesan layanan Klinnovac.\n\n` +
                            `Berikut detail pesanan Anda:\n` +
                            `Nomor Order: ${detailOrder.order_number}\n\n` +
                            `📋 Layanan:\n${itemLines}\n` +
                            `\n💰 Total: ${formatPrice(detailOrder.total)}` +
                            (totalDuration > 0 ? `\n⏱ Est. total pengerjaan: ${totalDuration} menit\n` : '\n') +
                            `\nTim kami akan segera menghubungi Anda.`
                          );
                          return `https://wa.me/${phone}?text=${text}`;
                        })()}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-brand hover:text-brand-dark flex items-center gap-1 font-medium"
                      >
                        <MessageCircle size={14} /> {detailOrder.customer_phone}
                      </a>
                    </div>
                    {detailOrder.customer_email && <div>
                      <p className="text-xs text-gray-400">Email</p>
                      <p className="text-sm">{detailOrder.customer_email}</p>
                    </div>}
                    {detailOrder.customer_address && <div>
                      <p className="text-xs text-gray-400">Alamat</p>
                      <p className="text-sm">{detailOrder.customer_address}</p>
                    </div>}
                    {detailOrder.notes && <div>
                      <p className="text-xs text-gray-400">Catatan</p>
                      <p className="text-sm">{detailOrder.notes}</p>
                    </div>}
                  </div>
                </div>

                {/* Referral */}
                {detailOrder.referral_code && (
                  <div className="bg-brand/5 border border-brand/20 rounded-lg p-3 flex items-center gap-2">
                    <QrCode size={16} className="text-brand" />
                    <p className="text-sm text-gray-700">Referral: <span className="font-mono font-bold">{detailOrder.referral_code}</span></p>
                  </div>
                )}

                {/* Status & Payment Control */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-gray-600 mb-3">⚙️ Kontrol Pesanan</h3>
                  <div className="space-y-3">
                    <div>
                      <p className="text-xs text-gray-400 mb-1">Status Pesanan</p>
                      <select
                        value={detailOrder.status}
                        onChange={e => handleStatusUpdate(detailOrder.id, 'status', e.target.value)}
                        className={`w-full px-3 py-2 text-sm rounded-lg border-0 font-medium ${statusColors[detailOrder.status] || 'bg-gray-100'}`}
                        disabled={saving}
                      >
                        {STATUSES.map(s => <option key={s} value={s}>{statusLabels[s] || s}</option>)}
                      </select>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 mb-1">Pembayaran</p>
                      <select
                        value={detailOrder.payment_status}
                        onChange={e => handleStatusUpdate(detailOrder.id, 'payment_status', e.target.value)}
                        className={`w-full px-3 py-2 text-sm rounded-lg border-0 font-medium ${paymentColors[detailOrder.payment_status] || 'bg-gray-100'}`}
                        disabled={saving}
                      >
                        {PAYMENT_STATUSES.map(s => <option key={s} value={s}>{paymentLabels[s] || s}</option>)}
                      </select>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 mb-1">Assign Pekerja</p>
                      <select
                        value={detailOrder.assigned_worker_id || ''}
                        onChange={e => { if (e.target.value) handleAssignWorker(detailOrder.id, parseInt(e.target.value)); }}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                        disabled={saving}
                      >
                        <option value="">- Pilih -</option>
                        {workers.filter(w => w.status === 'active').map(w => <option key={w.id} value={w.id}>{w.full_name}</option>)}
                      </select>
                      {detailOrder.assigned_worker && (
                        <p className="text-xs text-indigo-600 mt-1 flex items-center gap-1"><UserCheck size={12} /> {detailOrder.assigned_worker.full_name}</p>
                      )}
                      {!detailOrder.assigned_worker && detailOrder.assigned_worker_id && (
                        <p className="text-xs text-indigo-600 mt-1">ID: {detailOrder.assigned_worker_id}</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* RIGHT COLUMN: Items & Details */}
              <div className="space-y-4">
                {/* Items */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-gray-600 mb-3 flex items-center gap-1"><Tag size={14} /> Layanan</h3>
                  <div className="bg-white rounded-lg p-3 space-y-2 max-h-48 overflow-y-auto">
                    {(detailOrder.items || []).map((item, idx) => (
                      <div key={idx} className={`flex justify-between text-sm border-b pb-2 last:border-b-0 ${item.status === 'cancelled' ? 'opacity-60' : ''}`}>
                        <div className="flex-1">
                          <span className={item.status === 'cancelled' ? 'line-through text-gray-400' : 'text-gray-700 font-medium'}>{item.name}</span>
                          <div className="text-xs text-gray-500 mt-0.5">
                            Qty: {item.quantity}
                            {item.duration && <span className="ml-2 inline-flex items-center gap-0.5"><Clock size={10} /> {item.duration} menit</span>}
                          </div>
                          {item.status === 'cancelled' && (
                            <div className="mt-1 text-[10px] space-y-0.5">
                              <span className="text-red-500 font-semibold block">DIBATALKAN</span>
                              <span className="text-red-600 block">Oleh: {item.cancelled_by_name || 'N/A'}</span>
                              {item.cancelled_reason && <span className="text-gray-500 block">Alasan: {item.cancelled_reason}</span>}
                            </div>
                          )}
                        </div>
                        <span className={`font-semibold text-right ${item.status === 'cancelled' ? 'text-red-500 line-through' : 'text-brand'}`}>
                          {item.status === 'cancelled' ? '-' : ''}{formatPrice(item.unit_price * (item.quantity || 1))}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Summary */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-gray-600 mb-3">💰 Ringkasan</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Subtotal</span>
                      <span className="font-medium">{formatPrice(detailOrder.subtotal)}</span>
                    </div>
                    {detailOrder.tax_amount > 0 && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Pajak</span>
                        <span className="font-medium">{formatPrice(detailOrder.tax_amount)}</span>
                      </div>
                    )}
                    {detailOrder.discount_amount > 0 && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Diskon</span>
                        <span className="font-medium">-{formatPrice(detailOrder.discount_amount)}</span>
                      </div>
                    )}
                    <div className="border-t pt-2 flex justify-between text-base font-bold">
                      <span>Total</span>
                      <span className="text-brand">{formatPrice(detailOrder.total)}</span>
                    </div>
                  </div>
                </div>

                {/* Total duration estimate */}
                {(detailOrder.items || []).filter(item => item.status !== 'cancelled').some(item => item.duration) && (
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 flex items-center gap-2">
                    <Clock size={14} className="text-purple-600" />
                    <div className="text-sm">
                      <p className="text-xs text-gray-600">Est. total pengerjaan</p>
                      <p className="font-semibold text-purple-600">{(detailOrder.items || []).filter(item => item.status !== 'cancelled').reduce((sum, item) => sum + (parseInt(item.duration) || 0) * (item.quantity || 1), 0)} menit</p>
                    </div>
                  </div>
                )}

                {/* Invoice button if invoice_release or paid */}
                {(detailOrder.status === 'invoice_release' || detailOrder.status === 'paid') && (
                  <button onClick={() => setInvoiceOrder(detailOrder)} className="w-full py-3 bg-navy text-white rounded-lg text-sm font-medium flex items-center justify-center gap-2 hover:bg-navy/80 transition-colors">
                    <Printer size={16} /> Cetak Invoice
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Invoice Modal */}
      {invoiceOrder && <InvoiceModal order={invoiceOrder} onClose={() => setInvoiceOrder(null)} formatPrice={formatPrice} fmtDate={fmtDate} />}
    </AdminLayout>
  );
}